import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { storage, type AppSettings } from '@/store/storage'

interface SettingContextValue {
  settings: AppSettings
  updateSettings: (partial: Partial<AppSettings>) => void
  isDark: boolean
}

const SettingContext = createContext<SettingContextValue | null>(null)

function getSystemDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function SettingProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<AppSettings>(storage.getSettings())
  const isDark = settings.theme === 'dark' || (settings.theme === 'system' && getSystemDark())

  useEffect(() => {
    const root = document.documentElement
    if (isDark) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
  }, [isDark])

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => {
      if (settings.theme === 'system') {
        setSettings(s => ({ ...s }))
      }
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [settings.theme])

  const updateSettings = (partial: Partial<AppSettings>) => {
    const next = { ...settings, ...partial }
    setSettings(next)
    storage.setSettings(next)
  }

  return (
    <SettingContext.Provider value={{ settings, updateSettings, isDark }}>
      {children}
    </SettingContext.Provider>
  )
}

export function useSettings() {
  const ctx = useContext(SettingContext)
  if (!ctx) throw new Error('useSettings must be used within SettingProvider')
  return ctx
}
